import { humanize, badgeStyle } from '../../theme/adminVisualSystem';

// Status → tone mapping for the Invite OS.
const INVITE_TONE = {
  draft: 'muted',
  queued: 'softBlue',
  sent: 'softBlue',
  delivered: 'blue',
  opened: 'cyan',
  clicked: 'cyan',
  replied: 'violet',
  interested: 'violet',
  followup_1_due: 'amber',
  followup_2_due: 'amber',
  followup_1_sent: 'softBlue',
  followup_2_sent: 'softBlue',
  followup_due: 'amber',
  ticket_requested: 'cyan',
  ticket_accepted: 'green',
  converted: 'green',
  attended: 'green',
  interested_attending: 'green',
  not_interested: 'rose',
  declined: 'rose',
  paused: 'muted',
  bounced: 'rose',
  unsubscribed: 'rose',
  failed: 'rose',
  removed: 'muted',
  archived: 'muted',

  // Search result statuses
  new: 'softBlue',
  reviewed: 'cyan',
  saved: 'violet',
  rejected: 'muted',

  // Duplicate statuses
  exact_email: 'rose',
  exact_social: 'rose',
  exact_website: 'rose',
  possible_duplicate: 'amber',
  existing_contact: 'amber',
  already_invited: 'amber',
  already_ticketed: 'amber',
  suppressed: 'rose',

  // Priorities
  low: 'muted',
  normal: 'neutral',
  high: 'cyan',
  vip: 'gold',

  // Contact global status
  active: 'green',
  do_not_contact: 'rose',

  // Agent modes
  draft_only: 'cyan',
  auto: 'amber',
  manual: 'violet',

  // Relationship statuses
  prospect: 'softBlue',
  invited: 'cyan',
  cold: 'muted',
};

export default function InviteStatusBadge({ status, strong = false, className = '' }) {
  if (!status) {
    return (
      <span className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] text-[#555] border-[#222] ${className}`}>
        —
      </span>
    );
  }
  const toneKey = INVITE_TONE[status] || 'neutral';
  return (
    <span
      className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] ${className}`}
      style={badgeStyle(getToneObj(toneKey), strong)}
    >
      {humanize(status)}
    </span>
  );
}

// Resolve the tone object from a key — mirroring the palette in adminVisualSystem.js.
function getToneObj(key) {
  // Lazily resolve through badgeStyle: it accepts a tone object directly.
  // We hardcode the same key set here since they correspond directly.
  const T = {
    neutral: { text: '#d6d6d6', border: '#2a2a2a', bg: 'rgba(255,255,255,0.025)', glow: 'rgba(255,255,255,0.10)' },
    muted: { text: '#9a9a9a', border: '#333333', bg: 'transparent', glow: 'rgba(255,255,255,0.06)' },
    cyan: { text: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.08)', glow: 'rgba(144,224,239,0.35)' },
    softBlue: { text: '#7aa8d4', border: 'rgba(122,168,212,0.4)', bg: 'rgba(122,168,212,0.07)', glow: 'rgba(122,168,212,0.28)' },
    blue: { text: '#6ab6d4', border: 'rgba(106,182,212,0.4)', bg: 'rgba(106,182,212,0.08)', glow: 'rgba(106,182,212,0.3)' },
    violet: { text: '#c89dff', border: 'rgba(200,157,255,0.42)', bg: 'rgba(200,157,255,0.08)', glow: 'rgba(200,157,255,0.35)' },
    green: { text: '#7ed4a0', border: 'rgba(126,212,160,0.4)', bg: 'rgba(126,212,160,0.08)', glow: 'rgba(126,212,160,0.3)' },
    amber: { text: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.08)', glow: 'rgba(228,192,122,0.3)' },
    rose: { text: '#e88ba0', border: 'rgba(232,139,160,0.4)', bg: 'rgba(232,139,160,0.07)', glow: 'rgba(232,139,160,0.25)' },
    gold: { text: '#e6c562', border: 'rgba(230,197,98,0.45)', bg: 'rgba(230,197,98,0.08)', glow: 'rgba(230,197,98,0.35)' },
  };
  return T[key] || T.neutral;
}
